import React, { memo, useContext, useState, useCallback } from "react";
import { FieldType } from "../../constants";
import { hasValue } from "../../helper";
import { TypographyContent } from "../../atoms";
import { FormContext } from "../../organisms/Form/context";

import { Text } from "./Text";
import { TextArea } from "./TextArea";
import { Select } from "./Select";
import { Switch } from "./Switch";
import { TraitFrame } from "./TraitFrame";
import { CommonValues } from "./CommonValues";
import { ButtonControl } from "./ButtonControl";
import { DialogField } from "./Dialog";
import { DialogMultiField } from "./DialogMulti";
import { ObjectField } from "./Object";
import { Integer } from "./Integer";
import { Float } from "./Float";
import { DateTime } from "./DateTime";
import { DateRange } from "./DateRange";
import { Ordering } from "./Ordering";
import { JSONEditorField } from "./JSONEditor";
import { ErrorObject } from "./helper";

/**
 * Base constraint configuration for form field validation.
 * Used by fields that don't require specialized constraint types.
 */
export interface FormFieldConstraint {
  /**
   * Whether the field is required.
   */
  presence?: boolean;
  [key: string]: any;
}

/**
 * Re-export ErrorObject from helper for external use.
 */
export type FormFieldErrorObject = ErrorObject;

/**
 * Base props shared across all FormField components.
 * Provides common functionality for labels, validation, helpers, and styling.
 */
export interface BaseFormFieldProps {
  /**
   * Additional CSS class name(s) for the field container.
   */
  className?: string;
  /**
   * Unique identifier for the form field.
   * Used for accessibility and form data management.
   */
  id: string;
  /**
   * Label content to display above the field.
   * Supports localized content or plain strings.
   * @default "Label"
   */
  label?: TypographyContent;
  /**
   * Legacy localization key for the label.
   * @deprecated Use label prop instead
   */
  localKey?: string;
  /**
   * Array of validation errors for this field.
   * @default []
   */
  errors?: ErrorObject[];
  /**
   * Whether the field has been touched/interacted with.
   * Used to control when errors are displayed.
   * @default false
   */
  touched?: boolean;
  /**
   * Helper text to display below the field.
   * Provides additional context or instructions to the user.
   */
  helper?: TypographyContent;
  /**
   * Legacy localization key for helper text.
   * @deprecated Use helper prop instead
   */
  helperKey?: string;
  /**
   * Whether the field is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Makes the field non-editable while preserving its normal visual appearance.
   * Unlike disabled, the value can still be selected and copied.
   * @default false
   */
  readOnly?: boolean;
  /**
   * Maintains consistent height even when helper/error text is not shown.
   * Prevents layout shift when messages appear/disappear.
   * @default false
   */
  keepHeight?: boolean;
  /**
   * Hides the label element while maintaining accessibility.
   * @default false
   */
  noLabel?: boolean;
  /**
   * Reduces spacing and font sizes for a more compact appearance.
   * @default false
   */
  dense?: boolean;
}

const typeToField = {
  [FieldType.SELECT]: Select,
  [FieldType.SELECT_MULTI]: Select,
  [FieldType.TEXTAREA]: TextArea,
  [FieldType.BOOL]: Switch,
  [FieldType.TERMS]: Switch,
  [FieldType.INTEGER]: Integer,
  [FieldType.FLOAT]: Float,
  [FieldType.DATE]: DateTime,
  [FieldType.DATETIME]: DateTime,
  [FieldType.DATERANGE]: DateRange,
  ["date-range"]: DateRange,
  [FieldType.ORDERING]: Ordering,
  [FieldType.JSON]: JSONEditorField,
};

export interface FormFieldProps {
  id: string;
  type: FieldType;
  disabled?: boolean;
  editing?: boolean;
  fixed?: any;
  formId?: string;
  meta: FormFieldMeta;
  [key: string]: any;
}
export interface FormFieldMeta {
  component: React.ComponentType<any>;
  [key: string]: any;
}

const FormFieldComponent = ({
  type,
  disabled,
  editing,
  fixed,
  formId = "pss-form",
  label,
  localKey,
  ...props
}: FormFieldProps): JSX.Element => {
  const { customRenderers } = useContext(FormContext);
  const finalDisabled = disabled || hasValue(fixed);

  const resolvedLabel = label ?? (localKey ? { local: localKey } : undefined);

  const Field =
    type === FieldType.CUSTOM
      ? props.meta.component
      : customRenderers?.[type] || typeToField[type] || Text;

  return (
    <Field
      {...props}
      label={resolvedLabel}
      localKey={localKey}
      disabled={finalDisabled}
      uniqueId={`${formId}-${props.id}`}
      multi={type === FieldType.SELECT_MULTI ? true : undefined}
      {...(fixed ? { value: fixed } : {})}
      {...{ type }}
    />
  );
};

const MemoizedFormField = memo(FormFieldComponent);

MemoizedFormField.displayName = "FormField";

interface FormFieldCompoundType {
  (props: FormFieldProps): JSX.Element;
  Text: typeof Text;
  TextArea: typeof TextArea;
  Select: typeof Select;
  Switch: typeof Switch;
  Dialog: typeof DialogField;
  DialogMulti: typeof DialogMultiField;
  Object: typeof ObjectField;
  Integer: typeof Integer;
  Float: typeof Float;
  DateTime: typeof DateTime;
  DateRange: typeof DateRange;
  Ordering: typeof Ordering;
  JSONEditor: typeof JSONEditorField;
  TraitFrame: typeof TraitFrame;
  CommonValues: typeof CommonValues;
  ButtonControl: typeof ButtonControl;
}

const FormField = MemoizedFormField as unknown as FormFieldCompoundType;

FormField.Text = Text;
FormField.TextArea = TextArea;
FormField.Select = Select;
FormField.Switch = Switch;
FormField.Dialog = DialogField;
FormField.DialogMulti = DialogMultiField;
FormField.Object = ObjectField;
FormField.Integer = Integer;
FormField.Float = Float;
FormField.DateTime = DateTime;
FormField.DateRange = DateRange;
FormField.Ordering = Ordering;
FormField.JSONEditor = JSONEditorField;
FormField.TraitFrame = TraitFrame;
FormField.CommonValues = CommonValues;
FormField.ButtonControl = ButtonControl;

// Export standalone named versions
export { Text as FormFieldText };
export { TextArea as FormFieldTextArea };
export { Select as FormFieldSelect };
export { Switch as FormFieldSwitch };
export {
  DialogField,
  type FormFieldDialogProps,
  type DialogFieldValue,
} from "./Dialog";
export {
  DialogMultiField,
  type FormFieldDialogMultiProps,
  type DialogMultiFieldValue,
} from "./DialogMulti";
export {
  ObjectField,
  type FormFieldObjectProps,
  type ObjectFieldValue,
} from "./Object";
export {
  Integer as FormFieldInteger,
  type FormFieldIntegerProps,
} from "./Integer";
export { Float as FormFieldFloat, type FormFieldFloatProps } from "./Float";
export {
  DateTime as FormFieldDateTime,
  type FormFieldDateTimeProps,
  type DateTimeFieldConstraint,
} from "./DateTime";
export {
  DateRange as FormFieldDateRange,
  type FormFieldDateRangeProps,
  type DateRangeFieldConstraint,
  type QuickRange,
} from "./DateRange";
export {
  Ordering as FormFieldOrdering,
  type FormFieldOrderingProps,
  type OrderingFieldValue,
} from "./Ordering";
export {
  JSONEditorField,
  type FormFieldJSONEditorProps,
} from "./JSONEditor";
export { TraitFrame, type TraitFrameProps } from "./TraitFrame";
export {
  CommonValues,
  type CommonValuesProps,
  type CommonValueOption,
} from "./CommonValues";
export { ButtonControl, type ButtonControlProps } from "./ButtonControl";

export function useFieldValue<T, D = Record<string, any>>(
  initialValue: T
): {
  value: T;
  data: D | undefined;
  onChangeField: (id: string, value: T, opts?: { data?: D; [key: string]: any }) => void;
} {
  const [value, setValue] = useState<T>(initialValue);
  const [data, setData] = useState<D | undefined>(undefined);
  const onChangeField = useCallback(
    (_id: string, newValue: T, opts?: { data?: D; [key: string]: any }) => {
      setValue(newValue);
      if (opts?.data !== undefined) setData(opts.data);
    },
    []
  );
  return { value, data, onChangeField };
}

export { FormField };
